import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useState, useCallback } from 'react';

/**
 * Interface for technical competence payload
 */
export interface TechnicalCompetencePayload {
  assessment_id: string;
  competence1: string;
  competence2: string;
  competence3: string;
  competence4: string;
  competence5: string;
}

/**
 * Helper function to parse validation errors from API response
 */
const parseValidationErrors = (errorData: Record<string, string[]> | undefined): string => {
  if (!errorData || typeof errorData !== 'object') {
    return '';
  }

  const errors: string[] = [];

  for (const [field, messages] of Object.entries(errorData)) {
    if (Array.isArray(messages) && messages.length > 0) {
      const label = field.replace(/_/g, ' ');
      errors.push(`${label}: ${messages[0]}`);
    }
  }

  return errors.join('. ');
};

const submitTechnicalCompetenceAPI = async (payload: TechnicalCompetencePayload) => {
  const response = await apiClient.post('/assessment/technical-competence/', payload);
  return response.data;
};

export const useTechnicalCompetencePost = () => {
  const { openToast } = useToast();
  const queryClient = useQueryClient();
  const [data, setData] = useState<any>(null);
  const [status, setStatus] = useState<number>(0);
  const [error, setError] = useState<any>(null);

  const mutation = useMutation({
    mutationFn: submitTechnicalCompetenceAPI,
    onSuccess: (responseData, variables) => {
      setData(responseData);
      setStatus(200); // Success status
      setError(null);

      queryClient.invalidateQueries({
        queryKey: ['assessment', variables.assessment_id]
      });
      queryClient.invalidateQueries({
        queryKey: ['technical-competence', variables.assessment_id]
      });
    },
    onError: (err: any) => {
      // Try to extract specific validation errors from the response
      const validationErrors = parseValidationErrors(err.response?.data?.data);
      const genericMessage = err.response?.data?.message;

      // Prioritize specific validation errors, then generic message
      const errorMessage = validationErrors || genericMessage || 'Failed to submit technical competence questions';

      setError(err);
      setStatus(err.response?.status || 0);

      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 7000,
      });
    }
  });

  // Backwards compatible function
  const submitTechnicalCompetence = useCallback(
    async (payload: TechnicalCompetencePayload) => {
      try {
        const result = await mutation.mutateAsync(payload);
        return result;
      } catch (err) {
      }
    },
    [mutation]
  );

  return {
    // Backwards compatible interface
    data,
    status,
    loading: mutation.isPending,
    error,
    submitTechnicalCompetence,
    
    // Additional TanStack Query utilities
    mutation,
    reset: mutation.reset,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};